import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Play, Plus } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';
import { DraftOnlyLabel } from '@/components/pv/DraftOnlyLabel';

interface LiteratureSearch {
  id: string;
  name: string | null;
  source: string | null;
  query: string | null;
  lastRunAt: string | null;
  isActive: boolean;
  createdAt: string;
}

interface LiteratureHit {
  id: string;
  title: string | null;
  journal: string | null;
  publishedAt: string | null;
  classification: string | null;
  confidence: number | null;
  reviewStatus: string | null;
}

export function LiteraturePage() {
  const [tab, setTab] = useState<'searches' | 'hits'>('searches');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Literature surveillance</h1>
        <p className="text-sm text-muted-foreground">
          Scheduled PubMed / Embase searches with AI triage and human review before case promotion.
        </p>
      </div>

      <DraftOnlyLabel context="literature" />


      <div className="flex gap-1 border-b">
        {(
          [
            ['searches', 'Searches'],
            ['hits', 'Hits'],
          ] as ['searches' | 'hits', string][]
        ).map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === k
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === 'searches' ? <SearchesTab /> : <HitsTab />}
    </div>
  );
}

function SearchesTab() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = user && ROLE_RANK[user.role] >= ROLE_RANK['REVIEWER'];
  const [newOpen, setNewOpen] = useState(false);

  const searches = useQuery({
    queryKey: ['literature-searches'],
    queryFn: async () => {
      try {
        return (await api.get('/api/literature/searches?pageSize=100')).data;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  const runNow = useMutation({
    mutationFn: async (id: string) => api.post(`/api/literature/searches/${id}/run`, {}),
    onSuccess: () => {
      toast.success('Search queued', 'Results will appear under Hits');
      qc.invalidateQueries({ queryKey: ['literature-searches'] });
    },
    onError: (err) => toast.error('Run failed', extractApiError(err).message),
  });

  const items: LiteratureSearch[] = searches.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canManage && (
          <Button onClick={() => setNewOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> New search
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active searches</CardTitle>
          <CardDescription>Run weekly by default; can be triggered on demand.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Query</TableHead>
                <TableHead>Last run</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {searches.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!searches.isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="p-10 text-center text-sm text-muted-foreground">
                    <BookOpen className="mx-auto mb-2 h-6 w-6" /> No searches yet.
                  </TableCell>
                </TableRow>
              )}
              {items.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.name ?? '-'}</TableCell>
                  <TableCell className="text-xs">{s.source ?? '-'}</TableCell>
                  <TableCell className="max-w-md truncate font-mono text-xs">
                    {s.query ?? '-'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {s.lastRunAt ? formatDate(s.lastRunAt) : 'Never'}
                  </TableCell>
                  <TableCell>
                    {s.isActive ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Paused</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runNow.mutate(s.id)}
                      disabled={!canManage || runNow.isPending}
                    >
                      <Play className="mr-1 h-3 w-3" /> Run now
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <NewSearchDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={() => qc.invalidateQueries({ queryKey: ['literature-searches'] })}
      />
    </div>
  );
}

function HitsTab() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canReview = user && ROLE_RANK[user.role] >= ROLE_RANK['REVIEWER'];
  const [reviewFilter, setReviewFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');

  const hits = useQuery({
    queryKey: ['literature-hits', { reviewFilter, classFilter }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('pageSize', '100');
      if (reviewFilter) params.set('reviewStatus', reviewFilter);
      if (classFilter) params.set('classification', classFilter);
      try {
        return (await api.get(`/api/literature/hits?${params.toString()}`)).data;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  const review = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) =>
      api.patch(`/api/literature/hits/${id}`, { reviewStatus: status }),
    onSuccess: () => {
      toast.success('Review saved');
      qc.invalidateQueries({ queryKey: ['literature-hits'] });
    },
    onError: (err) => toast.error('Review failed', extractApiError(err).message),
  });

  const promote = useMutation({
    mutationFn: async (id: string) => api.post(`/api/literature/hits/${id}/promote`, {}),
    onSuccess: () => {
      toast.success('Promoted to PV case');
      qc.invalidateQueries({ queryKey: ['literature-hits'] });
    },
    onError: (err) => toast.error('Promote failed', extractApiError(err).message),
  });

  const items: LiteratureHit[] = hits.data?.items ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Review status</Label>
              <select
                value={reviewFilter}
                onChange={(e) => setReviewFilter(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All</option>
                <option value="PENDING">Pending</option>
                <option value="REPORTABLE">Reportable</option>
                <option value="NOT_REPORTABLE">Not reportable</option>
              </select>
            </div>
            <div>
              <Label>Classification</Label>
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">All</option>
                <option value="CASE_REPORT">Case report</option>
                <option value="REVIEW">Review</option>
                <option value="TRIAL">Trial</option>
                <option value="OBSERVATIONAL">Observational</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Hits</CardTitle>
          <CardDescription>
            Classification confidence from the AI triage. Human review required before promotion.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Journal</TableHead>
                <TableHead>Classification</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Review</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {hits.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!hits.isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="p-10 text-center text-sm text-muted-foreground">
                    <BookOpen className="mx-auto mb-2 h-6 w-6" /> No hits match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {items.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="max-w-md font-medium">
                    <div className="truncate">{h.title ?? '-'}</div>
                    {h.publishedAt && (
                      <div className="text-[10px] text-muted-foreground">
                        {formatDate(h.publishedAt)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{h.journal ?? '-'}</TableCell>
                  <TableCell>
                    {h.classification ? (
                      <Badge variant="info">{h.classification.replace(/_/g, ' ')}</Badge>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {h.confidence != null ? `${(h.confidence * 100).toFixed(0)}%` : '-'}
                  </TableCell>
                  <TableCell>
                    <ReviewBadge r={h.reviewStatus} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!canReview || review.isPending}
                        onClick={() => review.mutate({ id: h.id, status: 'REPORTABLE' })}
                      >
                        Reportable
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!canReview || review.isPending}
                        onClick={() => review.mutate({ id: h.id, status: 'NOT_REPORTABLE' })}
                      >
                        Not reportable
                      </Button>
                      <Button
                        size="sm"
                        disabled={!canReview || promote.isPending}
                        onClick={() => promote.mutate(h.id)}
                      >
                        Promote
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ReviewBadge({ r }: { r: string | null }) {
  if (!r) return <Badge variant="secondary">PENDING</Badge>;
  const v: Record<string, 'secondary' | 'info' | 'success' | 'warning' | 'destructive'> = {
    PENDING: 'warning',
    REPORTABLE: 'destructive',
    NOT_REPORTABLE: 'secondary',
  };
  return <Badge variant={v[r] ?? 'secondary'}>{r.replace(/_/g, ' ')}</Badge>;
}

function NewSearchDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({ name: '', source: 'PUBMED', query: '' });

  const create = useMutation({
    mutationFn: async () => api.post('/api/literature/searches', form),
    onSuccess: () => {
      toast.success('Search created');
      onOpenChange(false);
      setForm({ name: '', source: 'PUBMED', query: '' });
      onCreated();
    },
    onError: (err) => toast.error('Create failed', extractApiError(err).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New literature search</DialogTitle>
          <DialogDescription>Schedule a new PubMed / Embase surveillance query.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Drug X - weekly"
            />
          </div>
          <div>
            <Label>Source</Label>
            <select
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="PUBMED">PubMed</option>
              <option value="EMBASE">Embase</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div>
            <Label>Query</Label>
            <Textarea
              rows={4}
              value={form.query}
              onChange={(e) => setForm({ ...form, query: e.target.value })}
              placeholder='e.g. "Drug X"[tiab] AND (adverse[tiab] OR safety[tiab])'
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={create.isPending || !form.name || !form.query}
          >
            {create.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
